import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Loader,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { ClientInfoIndividualPageValidation, Customer } from 'src/front/api/apiInstance';
import { CardApplicationScreenType, ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';

import {
  containsLatinFIO,
  dictionaryLineFormat,
  formatAlphaNumericCyrillicSpecialCharacters,
  formatAlphaNumericCyrillicSpecialCharactersWithDot,
  formatFIONonResident,
  formatFreeText,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { IndividualInformationSchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components/Datepicker';

import {
  Container,
  Form,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  LineSeparator,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';

import {
  useCustomerSearchDispatch,
  useCustomerSearchState,
} from 'src/front/contexts/ClientInfoContext';

interface FormData {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  customerGender: Option | null;
  birthDate: Date | null;
  birthPlaceCountry: Option | null;
  citizenship: Option | null;
  birthPlaceRegion: Option | null;
  residenceCountry: Option | null;
  birthPlaceCity: string;
  hasPreviousSurname: Option | null;
  previousSurname: string;
  customerSurnameNonresident?: string;
  customerNameNonresident?: string;
  customerSecondNameNonresident?: string;
}

const defaultValues = {
  customerName: '',
  customerSurname: '',
  customerSecondName: '',
  customerGender: null,
  birthDate: null,
  birthPlaceCountry: null,
  citizenship: null,
  birthPlaceRegion: null,
  residenceCountry: null,
  birthPlaceCity: '',
  hasPreviousSurname: null,
  previousSurname: '',
  customerSurnameNonresident: '',
  customerNameNonresident: '',
  customerSecondNameNonresident: '',
};

interface Props {
  customerId?: string;
  customerNumber?: string;
  goToNextStep: (customerId?: string) => void;
  isEdit?: boolean;
  isEditCustomerName?: string;
}

const IndividualInformation: FC<Props> = ({ goToNextStep, isEdit, isEditCustomerName }) => {
  const { t } = useTranslation();

  const [isNonResident, setIsNonResident] = useState<boolean>(false);
  const customerSearchDispatch = useCustomerSearchDispatch();
  const resolver = useYupResolver(IndividualInformationSchema, {
    todayMaxControl: true,
  });
  const { customerId, clientName, customerNumber } = useCustomerSearchState();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [editCustomerInfo, setEditCustomerInfo] = useState<{
    customerId?: string;
    customerNumber?: string;
  }>();

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const genders = usePRM(ReferenceDataType.GENDER);
  const regions = usePRM(ReferenceDataType.REGION);
  const yesNoComboItems = usePRM(ReferenceDataType.YESNOTYPE);

  const { handleSubmit, errors, register, setValue, watch, control, formState } = useForm<FormData>(
    {
      defaultValues: {
        ...defaultValues,
      },
      resolver,
    }
  );

  const loadIndividualInformation = useCallback(async (): Promise<void> => {
    const { data } = await ClientInfoIndividualPageValidation.getClientInfoIndividualPageData();
    if (data.result === 'OK' && data.pageData) {
      const {
        customerGender,
        citizenship,
        birthDate,
        birthPlaceCity,
        residenceCountry,
        birthPlaceRegion,
        birthPlaceCountry,
        customerSurname,
        customerName,
        customerSecondName,
        hasPreviousSurname,
        previousSurname,
        customerSurnameNonresident,
        customerNameNonresident,
        customerSecondNameNonresident,
      } = data.pageData;
      if (isEdit) {
        setEditCustomerInfo({
          customerId: data.pageData.customerId,
          customerNumber: data.pageData.customerNumber,
        });
      }
      [
        { name: 'customerGender', value: customerGender ? optionFormat(customerGender) : '' },
        {
          name: 'birthPlaceCountry',
          value: birthPlaceCountry ? optionFormat(birthPlaceCountry) : '',
        },
        { name: 'citizenship', value: optionFormat(citizenship) },
        { name: 'residenceCountry', value: optionFormat(residenceCountry) },
        { name: 'customerName', value: customerName },
        { name: 'customerSurname', value: customerSurname },
        { name: 'customerSecondName', value: customerSecondName },
        { name: 'birthDate', value: convertStringToDate(birthDate) },
        { name: 'birthPlaceCity', value: birthPlaceCity },
        { name: 'birthPlaceRegion', value: optionFormat(birthPlaceRegion) },
        {
          name: 'hasPreviousSurname',
          value: hasPreviousSurname ? optionFormat(hasPreviousSurname) : '',
        },
        { name: 'previousSurname', value: previousSurname },
        { name: 'customerSurnameNonresident', value: customerSurnameNonresident },
        { name: 'customerNameNonresident', value: customerNameNonresident },
        { name: 'customerSecondNameNonresident', value: customerSecondNameNonresident },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    } else {
      const { data: response } = await Customer.getCustomerDetailForCardApplication({
        customerId: customerId || '',
        cardApplicationScreenType: CardApplicationScreenType.CLIENTINFOCHANGE,
      });
      const customerGender = genders.find((gender) => gender.value === response?.gender);
      const birthPlaceCountry = countries.find(
        (country) => country.value === response?.birthPlaceCountry
      );
      const citizenship = countries.find((country) => country.value === response?.citizenship);
      const birthPlaceRegion = regions.find(
        (region) => region.value === response?.birthPlaceRegion
      );
      const residenceCountry = countries.find((country) => country.value === response?.residence);
      [
        { name: 'customerGender', value: customerGender },
        { name: 'birthPlaceCountry', value: birthPlaceCountry },
        { name: 'citizenship', value: citizenship },
        { name: 'residenceCountry', value: residenceCountry },
        { name: 'customerName', value: response.customerName },
        { name: 'customerSurname', value: response.customerSurname },
        { name: 'customerSecondName', value: response.customerSecondName },
        { name: 'birthDate', value: convertStringToDate(response.birthDate) },
        { name: 'birthPlaceCity', value: response.birthPlaceText },
        { name: 'birthPlaceRegion', value: birthPlaceRegion },
        { name: 'customerSurnameNonresident', value: response.customerSurnameNonresident },
        { name: 'customerNameNonresident', value: response.customerNameNonresident },
        { name: 'customerSecondNameNonresident', value: response.customerSecondNameNonresident },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    }
    setIsLoading(false);
  }, [countries, customerId, genders, isEdit, regions, setValue]);

  useEffect(() => {
    register({ name: 'birthDate' });
    register({ name: 'birthPlaceRegion' });
    register({ name: 'newCustomer' });
  }, [register]);

  const handleDateChange = (date: Date): void =>
    setValue('birthDate', date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });
  const handleBirthPlaceRegion = (region: Option | null): void =>
    setValue('birthPlaceRegion', region, { shouldValidate: true, shouldDirty: true });

  const onSubmit = async (form: FormData): Promise<void> => {
    const response = await ClientInfoIndividualPageValidation.validateClientInfoIndividualPageData({
      customerId: customerId || editCustomerInfo?.customerId,
      customerNumber: customerNumber || editCustomerInfo?.customerNumber,
      customerSurname: form.customerSurname,
      customerName: form.customerName,
      customerSecondName: form.customerSecondName,
      customerGender: dictionaryLineFormat(form.customerGender),
      birthDate: formatDate(form.birthDate),
      birthPlaceCountry: dictionaryLineFormat(form.birthPlaceCountry),
      citizenship: dictionaryLineFormat(form.citizenship),
      birthPlaceRegion: dictionaryLineFormat(form.birthPlaceRegion),
      residenceCountry: dictionaryLineFormat(form.residenceCountry),
      birthPlaceCity: form.birthPlaceCity,
      ...(form.hasPreviousSurname
        ? { hasPreviousSurname: dictionaryLineFormat(form.hasPreviousSurname) }
        : { hasPreviousSurname: dictionaryLineFormat(yesNoComboItems[0]) }),
      previousSurname: form.previousSurname,
      customerSurnameNonresident: form.customerSurnameNonresident,
      customerNameNonresident: form.customerNameNonresident,
      customerSecondNameNonresident: form.customerSecondNameNonresident,
    });
    customerSearchDispatch({
      customerId: customerId || editCustomerInfo?.customerId,
    });
    if (response.data.result === 'OK') {
      goToNextStep();
    }
  };

  useEffect(() => {
    if (genders.length && countries.length && regions.length) {
      loadIndividualInformation();
    }
  }, [countries.length, customerId, genders.length, loadIndividualInformation, regions.length]);
  useEffect(() => {
    setIsNonResident(
      (watch('residenceCountry') !== null && watch('residenceCountry')?.value !== 'RU') ||
        containsLatinFIO(watch('customerSurname')) ||
        containsLatinFIO(watch('customerName')) ||
        containsLatinFIO(watch('customerSecondName'))
    );
  }, [watch]);

  return (
    <Container>
      <Title>
        <Trans i18nKey="changeFundClientInfo_title" />
      </Title>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="changeFundClientInfo_clientName_text" />:
        </InfoLabel>
        {isEditCustomerName || clientName?.clientName}
      </InfoRow>
      <FormRow>
        <LineSeparator />
      </FormRow>
      <Separator />
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="customerSurname"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.customerSurname?.message as string)}
                  formatter={formatAlphaNumericCyrillicSpecialCharacters}
                  label={t('changeFundClientInfo_surname_label')}
                  maxLength={60}
                  name="customerSurname"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="customerName"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.customerName?.message as string)}
                  formatter={formatAlphaNumericCyrillicSpecialCharacters}
                  label={t('changeFundClientInfo_name_label')}
                  maxLength={40}
                  name="customerName"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="customerSecondName"
              render={({ onChange, value }) => (
                <TextInput
                  formatter={formatAlphaNumericCyrillicSpecialCharactersWithDot}
                  label={t('changeFundClientInfo_middlename_label')}
                  maxLength={30}
                  name="customerSecondName"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="customerGender"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.customerGender as FieldError)?.message as string)}
                  items={genders}
                  label={t('changeFundClientInfo_gender_label')}
                  name="customerGender"
                  onChange={onChange}
                  value={value}
                  disabled
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Subtitle>
          <Trans i18nKey="changeFundClientInfo_birthPlace_label" />{' '}
        </Subtitle>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.birthDate?.message as string)}
              inputLabel={`${t('changeFundClientInfo_birthdate_label')} *`}
              onChange={handleDateChange}
              value={watch('birthDate') as Date}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="birthPlaceCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.birthPlaceCountry as FieldError)?.message as string)}
                  items={countries}
                  label={t('changeFundClientInfo_birthPlaceCountry_label')}
                  name="birthPlaceCountry"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirth_text')}
                  value={value}
                  disabled
                  required
                  searchable
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="citizenship"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.citizenship as FieldError)?.message as string)}
                  items={countries}
                  label={t('changeFundClientInfo_citizenship_label')}
                  name="citizenship"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_customerInfo_searchCitizenship_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FormElement>
            {watch('birthPlaceCountry')?.value === 'RU' ? (
              <Combobox
                errorMessage={t((errors?.birthPlaceRegion as FieldError)?.message as string)}
                items={regions}
                label={t('changeFundClientInfo_birthPlaceRegion_label')}
                name="birthPlaceRegion"
                onChange={handleBirthPlaceRegion}
                searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirthRegion_text')}
                value={watch('birthPlaceRegion')}
                disabled
                required
                searchable
              />
            ) : null}
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="residenceCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.residenceCountry as FieldError)?.message as string)}
                  items={countries}
                  label={t('changeFundClientInfo_residenceCountry_label')}
                  name="residenceCountry"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirth_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="birthPlaceCity"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.birthPlaceCity?.message as string)}
                  formatter={formatFreeText}
                  label={t('changeFundClientInfo_birthPlace_label')}
                  maxLength={100}
                  name="birthPlaceCity"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        {isNonResident ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_customerInfo_nonResident_text" />{' '}
            </Subtitle>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="customerSurnameNonresident"
                  render={({ onChange, value }) => (
                    <TextInput
                      errorMessage={t(errors?.customerSurnameNonresident?.message as string)}
                      formatter={formatFIONonResident}
                      label={t('cardEntry_customerInfo_surname_label')}
                      maxLength={150}
                      name="customerSurnameNonresident"
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
              </FormElement>
              <FormElement>
                <Controller
                  control={control}
                  name="customerNameNonresident"
                  render={({ onChange, value }) => (
                    <TextInput
                      errorMessage={t(errors?.customerNameNonresident?.message as string)}
                      formatter={formatFIONonResident}
                      label={t('cardEntry_customerInfo_name_label')}
                      maxLength={150}
                      name="customerNameNonresident"
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="customerSecondNameNonresident"
                  render={({ onChange, value }) => (
                    <TextInput
                      errorMessage={t(errors?.customerSecondNameNonresident?.message as string)}
                      formatter={formatFIONonResident}
                      label={t('cardEntry_customerInfo_middleName_label')}
                      maxLength={150}
                      name="customerSecondNameNonresident"
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default IndividualInformation;
